// ============================================================
// 루미큐브 SignalR 클라 wrapper  (SEQ-10214 S5c)
//   Hub 경로: /Hub/Rummikub  (TD api_contracts.hub_invokes §2 정합)
//   ticketHub.ts 패턴 100% 정합 (connection 단일·자동 reconnect·invoke 헬퍼)
//   메서드  8: StartGame·DrawTile·PlaceTile·MoveTile·JokerSwap·Undo·EndTurn·ReconnectGame
//   broadcast 15: TD hub_broadcasts 전부 핸들러 등록
// ============================================================

import * as signalR from '@microsoft/signalr'
import { rummikubStore } from '../store/rummikub'

const BASE_URL = process.env.REACT_APP_API_URL ?? ''

let connection: signalR.HubConnection | null = null
let lastToken: string | null = null

// SEQ=10222 자동 세트 통합: 같은 턴 내 직전 PLACE의 setId 추적 (다음 드롭 자동 재사용)
let _lastPlacedSetId: number = 0
export function getLastPlacedSetId(): number { return _lastPlacedSetId }
export function resetLastPlacedSetId(): void { _lastPlacedSetId = 0 }

// ── 연결 생성 / 반환 ──────────────────────────────────────
export function getConnection(token: string): signalR.HubConnection {
  lastToken = token
  if (connection && connection.state !== signalR.HubConnectionState.Disconnected) {
    return connection
  }
  connection = new signalR.HubConnectionBuilder()
    .withUrl(`${BASE_URL}/Hub/Rummikub?access_token=${encodeURIComponent(token)}`)
    .withAutomaticReconnect()
    .configureLogging(signalR.LogLevel.Warning)
    .build()
  return connection
}

// ── 연결 시작 / 보장 / 종료 ───────────────────────────────
export async function startConnection(token: string): Promise<void> {
  lastToken = token
  const conn = getConnection(token)
  if (conn.state === signalR.HubConnectionState.Disconnected) {
    await conn.start()
    registerBroadcastHandlers(conn)
  }
}

async function ensureConnected(): Promise<signalR.HubConnection> {
  if (!connection || connection.state === signalR.HubConnectionState.Disconnected) {
    if (!lastToken) throw new Error('RummikubHub 인증 토큰 없음 — 재로그인 필요')
    await startConnection(lastToken)
  }
  return connection!
}

export async function stopConnection(): Promise<void> {
  if (connection) {
    await connection.stop()
    connection = null
  }
}

export function isConnected(): boolean {
  return connection?.state === signalR.HubConnectionState.Connected
}

export function getState(): signalR.HubConnectionState | null {
  return connection?.state ?? null
}

// ── invoke 헬퍼 ──────────────────────────────────────────
async function invoke(method: string, ...args: unknown[]): Promise<unknown> {
  const conn = await ensureConnected()
  return conn.invoke(method, ...args)
}

// ════════════════════════════════════════════════════════
// 클라 → 서버 invoke 8종 (TD api_contracts.hub_invokes)
// ════════════════════════════════════════════════════════

/** 방장 게임 시작 (sp_RummikubGameStart + TilePoolInit + DealHands 일괄) */
export async function startGame(roomCode: string): Promise<void> {
  await invoke('StartGame', roomCode)
}

/** 자발적 카드 드로우 (working_state 변경 0건 검증, VOLUNTARY_DRAW) */
export async function drawTile(roomCode: string): Promise<void> {
  await invoke('DrawTile', roomCode)
}

/** 손패 → 보드 타일 배치 (targetSetId=0 = 신규 set 자동 생성) */
export async function placeTile(
  roomCode: string,
  tileId: number,
  targetSetId: number,
  position: number,
): Promise<void> {
  await invoke('PlaceTile', roomCode, tileId, targetSetId, position)
}

/** 보드 → 보드 타일 이동 (재구성, first_meld 완료 후만) */
export async function moveTile(
  roomCode: string,
  tileId: number,
  fromSetId: number,
  toSetId: number,
  position: number,
): Promise<void> {
  await invoke('MoveTile', roomCode, tileId, fromSetId, toSetId, position)
}

/** 조커 회수 (first_meld 완료 후만, GROUP set 모호 시 expectedFaceColor/Number 필수) */
export async function jokerSwap(
  roomCode: string,
  targetSetId: number,
  jokerPosition: number,
  replacementTileId: number,
  expectedFaceColor: string,
  expectedFaceNumber: number,
): Promise<void> {
  await invoke('JokerSwap', roomCode, targetSetId, jokerPosition, replacementTileId, expectedFaceColor, expectedFaceNumber)
}

/** 되돌리기 (working_state 초기화 → turn_start_snapshot 복원, SIMULATING → DRAW_OR_PLAY) */
export async function undo(roomCode: string): Promise<void> {
  await invoke('Undo', roomCode)
}

/** 턴 종료 (sp_RummikubEndTurn VALIDATE 5단계 발동) */
export async function endTurn(roomCode: string): Promise<void> {
  await invoke('EndTurn', roomCode)
}

/** 재연결 (sp_RummikubReconnect + 본인 스냅샷 재송출) */
export async function reconnectGame(roomCode: string): Promise<void> {
  await invoke('ReconnectGame', roomCode)
}

// ════════════════════════════════════════════════════════
// 서버 → 클라 broadcast 핸들러 15종 (TD api_contracts.hub_broadcasts)
//   Caller scope: HandDealt·TileDrawn·HandStateUpdated·GameSnapshot (4종 정보 비대칭 강제)
//   Group scope : 나머지 11종
// ════════════════════════════════════════════════════════

function registerBroadcastHandlers(conn: signalR.HubConnection): void {
  // Group: 게임 시작
  conn.on('GameStarted', (p: unknown) => {
    rummikubStore.applyGameStarted(p as RmkGameStartedPayload)
  })

  // Caller: 손패 배분 (정보 비대칭 — 본인 손패 face)
  // 예방적 안전망 (SEQ=10219): RoomHub 직접 broadcast 경로 포함, string 혼입 방어
  conn.on('HandDealt', (p: unknown) => {
    const obj = typeof p === 'string' ? JSON.parse(p) : p
    rummikubStore.applyHandDealt(obj as RmkHandDealtPayload)
  })

  // Caller: 타일 드로우 (정보 비대칭 — 본인 face + drawPileRemaining)
  conn.on('TileDrawn', (p: unknown) => {
    const obj = typeof p === 'string' ? JSON.parse(p) : p
    rummikubStore.applyTileDrawn(obj as RmkTileDrawnPayload)
  })

  // Group: 보드 업데이트 (배치/이동/제거 액션)
  conn.on('BoardUpdated', (p: unknown) => {
    const obj = typeof p === 'string' ? JSON.parse(p) : p
    const payload = obj as RmkBoardUpdatedPayload
    // SEQ=10222 자동 세트 통합: 본인 PLACE 시 마지막 setId 추적 (다음 드롭에 자동 재사용)
    if (payload.action === 'PLACE' && payload.setId && payload.setId > 0) {
      _lastPlacedSetId = payload.setId
    }
    rummikubStore.applyBoardUpdated(payload)
  })

  // Group: 보드 전체 스냅샷 (EndTurn 후 확정 상태)
  conn.on('BoardSnapshot', (p: unknown) => {
    rummikubStore.applyBoardSnapshot(p as RmkBoardSnapshotPayload)
  })

  // Group: 타인 손패 장수 업데이트 (face 비공개, 수량만)
  conn.on('HandSizeUpdated', (p: unknown) => {
    rummikubStore.applyHandSizeUpdated(p as RmkHandSizeUpdatedPayload)
  })

  // Caller: 본인 손패 상태 업데이트 (정보 비대칭 — face 포함)
  // 예방적 안전망 (SEQ=10219): string 혼입 방어
  conn.on('HandStateUpdated', (p: unknown) => {
    const obj = typeof p === 'string' ? JSON.parse(p) : p
    rummikubStore.applyHandStateUpdated(obj as RmkHandStateUpdatedPayload)
  })

  // Caller: 액션 에러 (서버 SendError) — SEQ=10222 fix: SignalR 핸들러 등록 누락 (warning 'No client method')
  conn.on('RummikubActionError', (p: unknown) => {
    const obj = typeof p === 'string' ? JSON.parse(p) : p
    const payload = obj as RmkActionErrorPayload
    _errorListeners.forEach(fn => { try { fn(payload) } catch { /* 무시 */ } })
  })

  // Group: 턴 종료 결과
  conn.on('TurnEnded', (p: unknown) => {
    _lastPlacedSetId = 0  // SEQ=10222 자동 세트 통합: 턴 종료 시 리셋
    rummikubStore.applyTurnEnded(p as RmkTurnEndedPayload)
  })

  // Group: 턴 전환
  conn.on('TurnChanged', (p: unknown) => {
    _lastPlacedSetId = 0  // SEQ=10222 자동 세트 통합: 턴 전환 시 리셋
    rummikubStore.applyTurnChanged(p as RmkTurnChangedPayload)
  })

  // Group: 조커 회수
  conn.on('JokerRetrieved', (p: unknown) => {
    rummikubStore.applyJokerRetrieved(p as RmkJokerRetrievedPayload)
  })

  // Group: 첫 진입(30점) 완료
  conn.on('InitialMeldCompleted', (p: unknown) => {
    rummikubStore.applyInitialMeldCompleted(p as RmkInitialMeldCompletedPayload)
  })

  // Group: 라운드 종료 + 만치카 점수
  conn.on('RoundEnded', (p: unknown) => {
    rummikubStore.applyRoundEnded(p as RmkRoundEndedPayload)
  })

  // Group: 매치 종료 (SINGLE 또는 CUMULATIVE 마지막 라운드)
  conn.on('GameEnded', (p: unknown) => {
    rummikubStore.applyGameEnded(p as RmkGameEndedPayload)
  })

  // Group: 플레이어 연결 상태 변경
  conn.on('PlayerConnectionChanged', (p: unknown) => {
    rummikubStore.applyPlayerConnectionChanged(p as RmkPlayerConnectionChangedPayload)
  })

  // Caller: 재연결 시 본인 전체 스냅샷 (정보 비대칭 — 본인 손패 포함)
  // catch B 클라 안전망 (SEQ=10219): 서버가 PayloadJson(string) 전송 시 JSON.parse, 객체면 그대로 사용
  conn.on('GameSnapshot', (p: unknown) => {
    const obj = typeof p === 'string' ? JSON.parse(p) : p
    rummikubStore.applyGameSnapshot(obj as RmkGameSnapshotPayload)
  })

  // Caller: Undo 후 본인 BOARD_WORKING 상태 (정보 비대칭 — 본인 시뮬레이션 중간 상태)
  // B-011 fix: 서버 RummikubHub.cs L620 WorkingStateUpdated Caller scope 정합
  conn.on('WorkingStateUpdated', (p: unknown) => {
    rummikubStore.applyWorkingStateUpdated(p as RmkWorkingStateUpdatedPayload)
  })

  // 강제 로그아웃 (공통 패턴)
  conn.on('ForceLogout', (msg: string) => {
    console.warn('[RummikubHub] ForceLogout', msg)
    window.dispatchEvent(new CustomEvent('session-expired'))
  })
}

// ── 이벤트 리스너 등록 헬퍼 (PlayRummikub 직접 등록용) ──────
type HubCallback = (...args: unknown[]) => void

export function on(event: string, callback: HubCallback): void {
  if (!connection) return
  connection.on(event, callback)
}

export function off(event: string, callback: HubCallback): void {
  if (!connection) return
  connection.off(event, callback)
}

// ── ActionError 외부 listener 큐 (ticketHub 패턴 정합) ──────
type ErrorListener = (p: RmkActionErrorPayload) => void
const _errorListeners: Set<ErrorListener> = new Set()

export function onActionError(fn: ErrorListener): () => void {
  _errorListeners.add(fn)
  return () => { _errorListeners.delete(fn) }
}

// ════════════════════════════════════════════════════════
// Payload 타입 (TD hub_broadcasts payload_schema 1:1)
// ════════════════════════════════════════════════════════

export interface RmkTile {
  tileId:  number
  color:   string  // BLACK|RED|BLUE|ORANGE|JOKER
  number:  number  // 1~13, JOKER=0
  isJoker: boolean
}

export interface RmkSetInfo {
  setId:    number
  setType:  string  // RUN|GROUP
  tileIds:  number[]
}

// BoardSnapshot boardTiles 배열 요소
export interface RmkBoardTileInfo {
  tileId:  number
  color:   string
  number:  number
  isJoker: boolean
}

export interface RmkScoreEntry {
  userCode:       number
  isWinner:       boolean
  handPenalty:    number
  roundScore:     number
  cumulativeAfter: number
}

export interface RmkFinalScoreEntry {
  userCode:        number
  cumulativeScore: number
  round1stCount:   number
  finalRank:       number
}

// broadcast payload 타입 선언
export interface RmkGameStartedPayload {
  matchId:             number
  playerCount:         number
  turnOrder:           number[]
  currentTurnUserCode: number
  turnDeadlineTs:      number
  drawPileRemaining:   number
  roundMode:           string
  totalRounds:         number
}

export interface RmkHandDealtPayload {
  matchId: number
  hand:    RmkTile[]
}

export interface RmkTileDrawnPayload {
  matchId:           number
  tileId:            number
  color:             string
  number:            number
  isJoker:           boolean
  drawPileRemaining: number
}

export interface RmkBoardUpdatedPayload {
  matchId:    number
  userCode:   number
  action:     string  // PLACE|MOVE|REMOVE
  tileId:     number
  fromSetId?: number
  toSetId?:   number
  setId?:     number
  position:   number
  // face 정보 (PLACE/MOVE 시 서버가 채워서 보냄 — boardTiles 클라 캐시용)
  tileColor?:  string
  tileNumber?: number
  isJoker?:    boolean
}

export interface RmkBoardSnapshotPayload {
  matchId:    number
  sets:       RmkSetInfo[]
  boardTiles: RmkBoardTileInfo[]  // face 배열 — boardTiles Map 갱신용
}

export interface RmkHandSizeUpdatedPayload {
  matchId:           number
  userCode:          number
  handSize:          number
  drawPileRemaining: number
}

export interface RmkHandStateUpdatedPayload {
  matchId: number
  hand:    RmkTile[]
}

export interface RmkTurnEndedPayload {
  matchId:              number
  userCode:             number
  result:               string  // VALID|INVALID|DRAW
  reason:               string
  meldValue?:           number
  isFirstMeldCompleted?: boolean
}

export interface RmkTurnChangedPayload {
  matchId:         number
  nextUserCode:    number
  turnDeadlineTs:  number
  turnNo:          number
}

export interface RmkJokerRetrievedPayload {
  matchId:           number
  userCode:          number
  setId:             number
  jokerTileId:       number
  replacementTileId: number
}

export interface RmkInitialMeldCompletedPayload {
  matchId:   number
  userCode:  number
  meldValue: number
}

export interface RmkRoundEndedPayload {
  matchId:        number
  roundNo:        number
  winnerUserCode: number
  scores:         RmkScoreEntry[]
}

export interface RmkGameEndedPayload {
  matchId:        number
  winnerUserCode: number
  finalScores:    RmkFinalScoreEntry[]
}

export interface RmkPlayerConnectionChangedPayload {
  matchId:          number
  userCode:         number
  state:            string  // CONNECTED|DISCONNECTED|PERMANENT_DISCONNECT
  graceDeadlineTs?: number
}

export interface RmkGameSnapshotPayload {
  matchId:             number
  gamePhase:           string
  currentTurnUserCode: number
  turnDeadlineTs:      number
  hand:                RmkTile[]
  boardSets:           RmkSetInfo[]
  drawPileRemaining:   number
  handSizes:           Array<{ userCode: number; handSize: number }>
  firstMeldStatus:     Array<{ userCode: number; completed: boolean }>
  cumulativeScores:    Array<{ userCode: number; score: number }>
}

export interface RmkActionErrorPayload {
  errorCode:    string
  errorMessage: string
}

// B-011 fix: WorkingStateUpdated 페이로드 (서버 RummikubHub.cs L620~L624 정합)
// GetWorkingStateSnapshot 반환값 = { setId, setType, tileIds[] } — RmkSetInfo와 동일 구조
export interface RmkWorkingStateUpdatedPayload {
  matchId:     number
  workingSets: RmkSetInfo[]
}
